import * as React from 'react';

const SCROLL_DEBOUNCE_MS = 100;

/**
 * Hook to save and restore scroll position for a custom scroll container.
 * Uses sessionStorage to persist scroll position across navigation.
 *
 * @param scrollContainerRef - Ref to the scrollable container element
 * @param storageKey - Unique key to store the scroll position in sessionStorage
 */
export function useScrollRestoration(
    scrollContainerRef: {
        current: HTMLDivElement | null;
    },
    storageKey: string
) {
    const scrollPositionRef = React.useRef<number>(0);

    // Restore scroll position on mount
    React.useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const savedPosition = sessionStorage.getItem(storageKey);
        if (savedPosition) {
            const position = parseInt(savedPosition, 10);
            if (!isNaN(position)) {
                // Use requestAnimationFrame to ensure the DOM is ready
                requestAnimationFrame(() => {
                    container.scrollTop = position;
                });
            }
        }
    }, [scrollContainerRef, storageKey]);

    // Save scroll position on scroll (debounced) and on unmount
    React.useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        let timeoutId: number;

        const handleScroll = () => {
            scrollPositionRef.current = container.scrollTop;

            clearTimeout(timeoutId);
            // @ts-expect-error
            timeoutId = setTimeout(() => {
                sessionStorage.setItem(storageKey, String(scrollPositionRef.current));
            }, SCROLL_DEBOUNCE_MS);
        };

        container.addEventListener('scroll', handleScroll, {passive: true});

        return () => {
            container.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
            // Save final position on unmount
            sessionStorage.setItem(storageKey, String(scrollPositionRef.current));
        };
    }, [scrollContainerRef, storageKey]);
}
